import {css, Collapse} from '@mui/material';
import {DlMuiDynamicIcon, useLocalStorage} from '@wallaby-ui/core';
import React, {useEffect} from 'react';

/** How long a group takes to open or close (ms). */
export const PANEL_GROUP_COLLAPSE_TIMEOUT = 250;

type Props = {
  iconName: string;
  viewBox: string;
  /** The translated group name, shown uppercase. */
  label: string;
  count: number;
  /** Local-storage key of the open/closed state. */
  storageKey: string;
  defaultOpen: boolean;
  /** The first group has no separator line above it. */
  first?: boolean;
  /** Opens a closed group as a click on its header would, the stored state included. */
  forceOpen?: boolean;
  /** `data-name` of the group; the header and the count carry it with `--header` and `--count`. */
  dataName: string;
  /** `data-group` of the group, for tests. */
  groupId?: string;
  children: React.ReactNode;
};

/* `.ghd` of the alerts design: icon, uppercase teal label, count badge, chevron at the right edge */
const headerCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 13px 4px 9px;
  border-top: 1px solid var(--dlAlertSeparator);
  cursor: pointer;
  user-select: none;
  color: var(--dlTheme-500);
  &[data-first='true'] {
    border-top: 0;
    padding-top: 6px;
  }
`;
const iconCss = css`
  font-size: 15px;
`;
const labelCss = css`
  font-family: var(--dlFontFamilySemiBold);
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;
const countCss = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  box-sizing: border-box;
  border-radius: 5px;
  background: var(--dlTheme-50);
  color: var(--dlTheme-700);
  font-family: var(--dlFontFamilySemiBold);
  font-size: 11px;
`;
const chevronCss = css`
  margin-left: auto;
  display: flex;
  color: var(--dlThemeGray-300);
  & svg {
    width: 12px;
    height: 8px;
    display: block;
    transition: transform 0.2s ease;
  }
  &[data-open='true'] svg {
    transform: rotate(180deg);
  }
`;
const rowsCss = css`
  display: flex;
  flex-direction: column;
`;

/**
 * One collapsible group of a right-panel tab (the Alerts panel's episode groups, the risk factors' Active and
 * Deactivated): a clickable header with the group's icon, name and count, its rows below. The open state is kept
 * in local storage per page.
 */
export const LightPanelGroup: React.FC<Props> = (props) => {
  const {iconName, viewBox, label, count, storageKey, defaultOpen, first, forceOpen, dataName, groupId, children} = props;
  const [open, _, setOpen] = useLocalStorage<boolean>(storageKey, defaultOpen);
  useEffect(() => {
    if (forceOpen && !open) setOpen(true);
  }, [forceOpen, open, setOpen]);
  return (
    <div data-name={dataName} data-group={groupId}>
      <div css={headerCss} data-first={Boolean(first)} onClick={() => setOpen(!open)} data-name={`${dataName}--header`}>
        <DlMuiDynamicIcon
          iconName={iconName}
          useFixedViewBox={viewBox}
          noButtonStyles
          style={{color: 'inherit', padding: 0, margin: 0, pointerEvents: 'none'}}
          svgIconInputCss={iconCss}
        />
        <span css={labelCss}>{label}</span>
        <span css={countCss} data-name={`${dataName}--count`}>
          {count}
        </span>
        <span css={chevronCss} data-open={Boolean(open)}>
          <DlMuiDynamicIcon
            iconName='expandMoreIcon'
            useFixedViewBox='7 8.75 10 6.5'
            noButtonStyles
            style={{color: 'inherit', padding: 0, margin: 0, pointerEvents: 'none'}}
          />
        </span>
      </div>
      <Collapse in={Boolean(open)} timeout={PANEL_GROUP_COLLAPSE_TIMEOUT}>
        <div css={rowsCss}>{children}</div>
      </Collapse>
    </div>
  );
};

LightPanelGroup.displayName = 'LightPanelGroup';
